/**
 * `CustomerLedgerPage` — chronological invoices + receipts + credit
 * notes for one customer with a running balance column.
 *
 * Balance sign: positive = customer owes us (open receivable);
 * negative = customer has advance / credit with us.
 *
 * @see packages/domain/src/sales/customers/get-customer-ledger.ts
 */
import { Download } from "lucide-react";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { BackButton } from "../../workspace/components/back-button.tsx";

type Customer = z.infer<typeof contract.customerRow>;
type Ledger = z.infer<typeof contract.ledgerOutput>;
type LedgerRow = z.infer<typeof contract.ledgerRow>;

export function CustomerLedgerPage({
  customer,
  ledger,
}: {
  customer: Customer;
  ledger: Ledger;
}) {
  const closing = BigInt(ledger.closingBalancePaise);
  return (
    <div className="customer-ledger-page">
      <header className="customer-ledger-head">
        <div className="customer-ledger-title">
          <BackButton
            fallbackTo={`/sales/customers/${customer.id}`}
            label={`Back to ${customer.name}`}
          />
          <div>
            <h1>Ledger — {customer.name}</h1>
            <p className="customer-ledger-sub">
              Chronological view of invoices, receipts, and credit notes for this
              customer.
            </p>
          </div>
        </div>
        <div className="customer-ledger-header-actions">
          <button
            type="button"
            className="customer-detail-ghost"
            onClick={() => void exportCustomerStatementXlsx(customer, ledger)}
            disabled={ledger.rows.length === 0}
            title={
              ledger.rows.length === 0 ? "No ledger activity to export yet" : undefined
            }
            aria-disabled={ledger.rows.length === 0 || undefined}
          >
            <Download size={13} strokeWidth={2.2} aria-hidden />
            Download Excel
          </button>
        </div>
        <div className="customer-ledger-summary">
          <div>
            <span className="lbl">Opening balance</span>
            <span className="val">{formatSignedPaise(ledger.openingBalancePaise)}</span>
            {customer.openingBalanceAsOf ? (
              <span className="asof">as of {customer.openingBalanceAsOf}</span>
            ) : null}
          </div>
          <div>
            <span className="lbl">Closing balance</span>
            <span className={`val ${closing < 0n ? "credit" : closing > 0n ? "debit" : ""}`}>
              {formatSignedPaise(ledger.closingBalancePaise)}
            </span>
            <span className="asof">
              {closing < 0n
                ? "customer has advance"
                : closing > 0n
                  ? "customer owes"
                  : "settled"}
            </span>
          </div>
        </div>
      </header>

      {ledger.rows.length === 0 ? (
        <div className="customer-ledger-empty">
          <p>No ledger activity yet.</p>
          <p className="customer-ledger-sub">
            Once this customer has an invoice, receipt, or credit note, entries
            will appear here in date order with a running balance.
          </p>
        </div>
      ) : (
        <div className="customer-ledger-table-wrap">
          <table className="customer-ledger-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Doc</th>
                <th scope="col">Kind</th>
                <th scope="col" className="num">
                  Debit
                </th>
                <th scope="col" className="num">
                  Credit
                </th>
                <th scope="col" className="num">
                  Running
                </th>
              </tr>
            </thead>
            <tbody>
              {ledger.rows.map((r) => (
                <LedgerBodyRow key={`${r.kind}-${r.entryId}`} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LedgerBodyRow({ row }: { row: LedgerRow }) {
  const voidRow = row.kind === "invoice" && row.status === "void";
  const balance = BigInt(row.runningBalancePaise);
  return (
    <tr className={voidRow ? "voided" : undefined}>
      <td className="date">{row.at}</td>
      <td className="doc">
        {row.docNumber || <span className="text-muted">—</span>}
        {row.kind === "invoice" && row.status ? (
          <span className={`customer-ledger-status ${row.status}`}>{row.status}</span>
        ) : null}
      </td>
      <td className="kind">{kindLabel(row.kind)}</td>
      <td className="num">
        {row.debitPaise === "0" ? (
          <span className="text-muted">—</span>
        ) : (
          formatPaise(row.debitPaise)
        )}
      </td>
      <td className="num">
        {row.creditPaise === "0" ? (
          <span className="text-muted">—</span>
        ) : (
          formatPaise(row.creditPaise)
        )}
      </td>
      <td className={`num running ${balance < 0n ? "credit" : ""}`}>
        {formatSignedPaise(row.runningBalancePaise)}
      </td>
    </tr>
  );
}

function kindLabel(kind: LedgerRow["kind"]): string {
  switch (kind) {
    case "invoice":
      return "Invoice";
    case "receipt":
      return "Receipt";
    case "credit_note":
      return "Credit note";
  }
}

// Local formatters — the exact-rupee shape the ledger needs is
// distinct from `formatPaiseShort` (which collapses ≥1L to "1.20L").
// Ledgers show full commaed amounts so a reconciliation eye can
// match against a bank statement.
function formatPaise(value: string | bigint): string {
  const paise = typeof value === "bigint" ? value : BigInt(value);
  const rupees = Number(paise) / 100;
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rupees)}`;
}

function formatSignedPaise(value: string | bigint): string {
  const paise = typeof value === "bigint" ? value : BigInt(value);
  if (paise === 0n) return "₹0";
  const abs = paise < 0n ? -paise : paise;
  const label = formatPaise(abs);
  return paise < 0n ? `−${label}` : label;
}

/**
 * Convert the ledger + customer into a fully styled `.xlsx` customer
 * statement — meant to look like a real business document when opened
 * in Excel or Google Sheets:
 *
 *   • Big bold merged title row.
 *   • Info block (GSTIN, address, date, opening balance) with bold
 *     labels and a wide-enough label column so nothing truncates.
 *   • Ledger table with a filled + bordered header, per-cell borders
 *     on the body, and Indian lakh-grouped `₹#,##,##0.00` formatting
 *     on the money columns.
 *   • Closing balance highlighted in bold with a top border.
 *   • Column widths tuned so dates + docs don't render as `#####`.
 *   • Header row frozen so long ledgers stay scannable.
 *
 * ExcelJS is lazy-loaded so the ~700 KB library only lands in the
 * bundle when an operator actually clicks the export button.
 * Amounts are stored as real rupee numbers so Excel treats them as
 * numeric and totals/sorts work.
 */
export async function exportCustomerStatementXlsx(
  customer: Customer,
  ledger: Ledger,
): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;

  const paiseToRupees = (v: string): number => Number(BigInt(v)) / 100;
  const addressParts = [
    customer.addressLine1,
    customer.city,
    customer.state,
    customer.pincode,
  ].filter((s): s is string => Boolean(s && s.length));
  const todayIso = new Date().toISOString().slice(0, 10);

  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  wb.created = new Date();
  const ws = wb.addWorksheet("Statement", {
    views: [{ state: "frozen", ySplit: 7 }],
  });

  // ── Column widths ──────────────────────────────────────────────────
  // Column A is intentionally wide so labels like "Opening balance (INR)"
  // fit without truncation. The rest are tuned for the ledger table.
  ws.columns = [
    { width: 24 }, // A — Date / labels
    { width: 18 }, // B — Kind / values
    { width: 22 }, // C — Doc
    { width: 18 }, // D — Debit
    { width: 18 }, // E — Credit
    { width: 20 }, // F — Running
    { width: 12 }, // G — Status
  ];

  // ── Row 1 — big merged title ───────────────────────────────────────
  ws.mergeCells("A1:G1");
  const title = ws.getCell("A1");
  title.value = `Customer statement — ${customer.name}`;
  title.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FF0F172A" } };
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  // ── Rows 2-5 — info block (bold label in A, value in B) ────────────
  const infoRows: Array<[string, string | number]> = [
    ["GSTIN", customer.gstin ?? "—"],
    ["Address", addressParts.join(", ") || "—"],
    ["Generated", todayIso],
    ["Opening balance (INR)", paiseToRupees(ledger.openingBalancePaise)],
  ];
  infoRows.forEach(([label, value], i) => {
    const row = ws.getRow(2 + i);
    row.getCell(1).value = label;
    row.getCell(1).font = { bold: true, color: { argb: "FF475569" } };
    row.getCell(2).value = value;
    if (typeof value === "number") {
      row.getCell(2).numFmt = INR_FORMAT;
    }
  });

  // ── Row 7 — ledger table header (filled + bold + bordered) ─────────
  const headerLabels = [
    "Date",
    "Kind",
    "Doc",
    "Debit (INR)",
    "Credit (INR)",
    "Running (INR)",
    "Status",
  ];
  const headerRow = ws.getRow(7);
  headerLabels.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F172A" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = {
      top: { style: "thin", color: { argb: "FF0F172A" } },
      bottom: { style: "thin", color: { argb: "FF0F172A" } },
      left: { style: "thin", color: { argb: "FFCBD5E1" } },
      right: { style: "thin", color: { argb: "FFCBD5E1" } },
    };
  });
  headerRow.height = 22;

  // ── Rows 8+ — ledger body ──────────────────────────────────────────
  ledger.rows.forEach((r, i) => {
    const row = ws.getRow(8 + i);
    row.getCell(1).value = r.at;
    row.getCell(2).value = kindLabel(r.kind);
    row.getCell(3).value = r.docNumber || "—";
    row.getCell(4).value = paiseToRupees(r.debitPaise);
    row.getCell(5).value = paiseToRupees(r.creditPaise);
    row.getCell(6).value = paiseToRupees(r.runningBalancePaise);
    row.getCell(7).value = r.status ?? "";

    // INR format on money columns.
    row.getCell(4).numFmt = INR_FORMAT;
    row.getCell(5).numFmt = INR_FORMAT;
    row.getCell(6).numFmt = INR_FORMAT;

    // Body borders.
    for (let c = 1; c <= 7; c++) {
      row.getCell(c).border = {
        top: { style: "hair", color: { argb: "FFE2E8F0" } },
        bottom: { style: "hair", color: { argb: "FFE2E8F0" } },
        left: { style: "hair", color: { argb: "FFE2E8F0" } },
        right: { style: "hair", color: { argb: "FFE2E8F0" } },
      };
    }

    // Void invoice — grey out the row so it's honest but visibly retired.
    if (r.kind === "invoice" && r.status === "void") {
      for (let c = 1; c <= 7; c++) {
        row.getCell(c).font = { color: { argb: "FF94A3B8" }, strike: true };
      }
    }
  });

  // ── Closing balance — highlighted row after a blank spacer ─────────
  const closingRow = ws.getRow(8 + ledger.rows.length + 1);
  closingRow.getCell(1).value = "Closing balance (INR)";
  closingRow.getCell(1).font = { bold: true, size: 12 };
  closingRow.getCell(1).border = {
    top: { style: "medium", color: { argb: "FF0F172A" } },
  };
  closingRow.getCell(2).value = paiseToRupees(ledger.closingBalancePaise);
  closingRow.getCell(2).font = { bold: true, size: 12 };
  closingRow.getCell(2).numFmt = INR_FORMAT;
  closingRow.getCell(2).border = {
    top: { style: "medium", color: { argb: "FF0F172A" } },
  };

  // ── Serialise + trigger download ───────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `statement-${slug(customer.name)}-${todayIso}.xlsx`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const INR_FORMAT = '"₹"#,##,##0.00;"-₹"#,##,##0.00';

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-|-$/g, "") || "customer"
  );
}
